export type PresentationLayout = 'screen' | 'camera' | 'screen-only' | 'side-by-side';

/** One video track fits the existing admitted-speaker protocol; microphones stay separate. */
export class ScreenPresentation {
  readonly stream: MediaStream;
  private canvas = document.createElement('canvas');
  private screen = document.createElement('video');
  private camera = document.createElement('video');
  private cameraTrack: MediaStreamTrack | undefined;
  private layout: PresentationLayout = 'screen';
  private timer: ReturnType<typeof setInterval>;
  private closed = false;
  private frame: CanvasCaptureMediaStreamTrack;

  constructor(display: MediaStream) {
    this.canvas.width = 1280;
    this.canvas.height = 720;
    if (!this.canvas.captureStream) throw Error('Screen presentation is unavailable in this browser.');
    for (const video of [this.screen, this.camera]) {
      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;
    }
    this.screen.srcObject = display;
    void this.screen.play().catch(() => {});
    let capture = this.canvas.captureStream(0);
    if (typeof (capture.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack).requestFrame !== 'function') {
      capture.getTracks().forEach(t => t.stop());
      capture = this.canvas.captureStream(25);
    }
    this.stream = capture;
    this.frame = this.stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
    this.timer = setInterval(() => this.draw(), 40);
    this.draw();
  }

  setSource(stream: MediaStream | null) {
    this.screen.srcObject = stream;
    if (stream) void this.screen.play().catch(() => {});
    this.draw();
  }

  setCamera(stream: MediaStream) {
    const track = stream.getVideoTracks().find(t => t.readyState === 'live');
    if (this.cameraTrack === track) return;
    this.cameraTrack = track;
    this.camera.srcObject = track ? new MediaStream([track]) : null;
    if (track) void this.camera.play().catch(() => {});
    this.draw();
  }

  setLayout(layout: PresentationLayout) { this.layout = layout; this.draw(); }

  private draw() {
    if (this.closed) return;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#080b10';
    ctx.fillRect(0, 0, 1280, 720);
    const paint = (video: HTMLVideoElement, x: number, y: number, w: number, h: number) => {
      ctx.fillStyle = '#172433';
      ctx.fillRect(x, y, w, h);
      if (video.readyState < 2 || !video.videoWidth) return;
      const scale = Math.min(w / video.videoWidth, h / video.videoHeight);
      const vw = video.videoWidth * scale, vh = video.videoHeight * scale;
      ctx.drawImage(video, x + (w - vw) / 2, y + (h - vh) / 2, vw, vh);
    };
    const camera = this.cameraTrack?.readyState === 'live' && this.cameraTrack.enabled && !this.cameraTrack.muted;
    if (this.layout === 'side-by-side' && camera) {
      paint(this.screen, 0, 0, 864, 720);
      paint(this.camera, 880, 0, 400, 720);
    } else {
      const cameraMain = this.layout === 'camera' && camera;
      paint(cameraMain ? this.camera : this.screen, 0, 0, 1280, 720);
      if (camera && this.layout !== 'screen-only') {
        ctx.fillStyle = '#65d7e7';
        ctx.fillRect(968, 516, 288, 168);
        paint(cameraMain ? this.screen : this.camera, 972, 520, 280, 160);
      }
    }
    this.frame.requestFrame?.();
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    clearInterval(this.timer);
    this.stream.getTracks().forEach(t => t.stop());
    for (const video of [this.screen, this.camera]) { video.pause(); video.srcObject = null; }
    // The caller owns display capture and camera/microphone tracks.
  }
}
